import React, { useRef } from "react";
import { useNavigate, Link } from "react-router-dom";
import { useApp } from "../context/AppContext";
import { useScrollReveal } from "../lib/useScrollReveal";
import { useScrollParallax } from "../lib/useScrollParallax";
import Seo from "../components/Seo";
import { MarketingNav, MarketingFooter, FaqItem, L } from "../components/marketing/MarketingChrome";
import { TEMPLATES, SPACES_BY_KEY, getSpaceIcon, getTemplateSpaceKeys } from "../lib/templates";
import {
  Bell, KanbanSquare, Wallet, Users2, Sparkles, ArrowRight, Check,
  ShieldCheck, UserRound, Building2, Boxes, Target, TrendingUp, Layers, Upload, PenLine, Briefcase,
  Zap, KeyRound,
} from "lucide-react";

const ACCENTS = ["#2563EB", "#10B981", "#EA580C", "#F59E0B"];

// CSS-built cockpit dashboard mock for the hero (no static image — crisp + on-brand).
function HeroMock({ lang }) {
  return (
    <div className="relative" data-testid="hero-mock">
      <div className="absolute -inset-6 bg-gradient-to-tr from-blue-500/15 via-transparent to-emerald-500/10 blur-3xl rounded-[2rem]" />
      <div className="relative rounded-2xl border border-slate-200 bg-white shadow-[0_20px_60px_-15px_rgba(15,23,42,0.18)] overflow-hidden">
        <div className="flex items-center gap-1.5 px-4 py-2.5 border-b border-slate-200 bg-slate-50">
          <span className="w-2.5 h-2.5 rounded-full bg-red-400" />
          <span className="w-2.5 h-2.5 rounded-full bg-amber-400" />
          <span className="w-2.5 h-2.5 rounded-full bg-emerald-400" />
          <span className="ml-3 text-[10px] font-mono text-slate-400">cockpit-os.com</span>
        </div>
        <div className="p-4">
          <div className="flex items-center justify-between mb-3">
            <div className="text-xs font-heading font-semibold text-slate-900">{L(lang, "Votre cockpit", "Your cockpit")}</div>
            <div className="flex gap-1.5">
              {ACCENTS.map((c) => <span key={c} className="w-2 h-2 rounded-full" style={{ background: c }} />)}
            </div>
          </div>
          <div className="grid grid-cols-3 gap-2 mb-3">
            {[
              { l: L(lang, "Trésorerie", "Cash flow"), v: "€128k", c: "#10B981" },
              { l: L(lang, "Patrimoine", "Net worth"), v: "€2.4M", c: "#2563EB" },
              { l: L(lang, "Tâches", "Open tasks"), v: "37", c: "#EA580C" },
            ].map((k) => (
              <div key={k.l} className="rounded-lg border border-slate-200 bg-slate-50/60 p-2.5">
                <div className="text-[9px] text-slate-400 mb-1">{k.l}</div>
                <div className="text-sm font-semibold tabular-nums" style={{ color: k.c }}>{k.v}</div>
              </div>
            ))}
          </div>
          <div className="rounded-lg border border-slate-200 p-3 mb-3">
            <div className="flex items-end gap-1.5 h-16">
              {[40, 55, 35, 70, 50, 85, 65, 95, 75, 60, 88, 100].map((h, i) => (
                <div key={i} className="flex-1 rounded-sm" style={{ height: `${h}%`, background: i % 4 === 0 ? "#2563EB" : "rgba(37,99,235,0.25)" }} />
              ))}
            </div>
          </div>
          <div className="space-y-1.5">
            {[
              { e: "#2563EB", t: L(lang, "Relancer un prospect", "Follow up with a lead") },
              { e: "#10B981", t: L(lang, "Clôturer une offre client", "Close a client deal") },
              { e: "#EA580C", t: L(lang, "Échéance TVA dans 9 jours", "VAT deadline in 9 days") },
            ].map((r) => (
              <div key={r.t} className="flex items-center gap-2 rounded-md border border-slate-200 px-2.5 py-1.5">
                <span className="w-1.5 h-1.5 rounded-full shrink-0" style={{ background: r.e }} />
                <span className="text-[11px] text-slate-700 truncate">{r.t}</span>
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}

function FeatureRow({ icon: Icon, accent, eyebrow, title, body, points, reverse, visual }) {
  return (
    <div className={`grid lg:grid-cols-2 gap-10 lg:gap-16 items-center ${reverse ? "lg:[direction:rtl]" : ""}`}>
      <div className="[direction:ltr]" data-parallax={reverse ? "right" : "left"}>
        <div className="inline-flex items-center gap-2 text-xs font-mono uppercase tracking-[0.15em] mb-4" style={{ color: accent }}>
          <Icon className="w-4 h-4" /> {eyebrow}
        </div>
        <h3 className="font-heading font-bold text-2xl sm:text-3xl tracking-tight text-slate-900 mb-4">{title}</h3>
        <p className="text-slate-600 leading-relaxed mb-5">{body}</p>
        <ul className="space-y-2.5">
          {points.map((p) => (
            <li key={p} className="flex items-start gap-2.5 text-sm text-slate-700">
              <Check className="w-4 h-4 mt-0.5 shrink-0" style={{ color: accent }} /> {p}
            </li>
          ))}
        </ul>
      </div>
      <div className="[direction:ltr]" data-parallax={reverse ? "left" : "right"}>{visual}</div>
    </div>
  );
}

function MiniVisual({ children }) {
  return <div className="rounded-3xl border border-slate-200 bg-slate-50 p-5 shadow-sm">{children}</div>;
}

function PersonaCard({ icon: Icon, accent, title, body }) {
  return (
    <div className="rounded-2xl border border-slate-200 bg-white p-6 shadow-sm hover:-translate-y-1 hover:shadow-lg transition-all duration-300" data-testid={`persona-${title}`}>
      <div className="w-11 h-11 rounded-xl flex items-center justify-center mb-4" style={{ background: `${accent}14`, color: accent }}>
        <Icon className="w-5 h-5" />
      </div>
      <h3 className="font-heading font-bold text-lg text-slate-900 mb-2">{title}</h3>
      <p className="text-sm text-slate-600 leading-relaxed">{body}</p>
    </div>
  );
}

export default function Landing() {
  const { lang, user } = useApp();
  const navigate = useNavigate();
  const rootRef = useRef(null);
  useScrollReveal(rootRef, lang);
  useScrollParallax(rootRef, lang);
  const go = () => navigate(user ? "/" : "/login?signup=1");

  const personas = [
    { icon: UserRound, accent: "#2563EB", title: L(lang, "QG personnel", "Personal HQ"),
      body: L(lang, "Organisez vos objectifs, tâches, documents, actifs et décisions dans un cockpit privé.",
                    "Organize your goals, tasks, documents, assets, and decisions in one private cockpit.") },
    { icon: Building2, accent: "#10B981", title: L(lang, "QG d'entreprise", "Business HQ"),
      body: L(lang, "Pilotez une entreprise avec CRM, projets, factures, documents, OKR, alertes et gestion du travail.",
                    "Run one business with CRM, projects, invoices, documents, OKRs, alerts, and work management.") },
    { icon: Boxes, accent: "#EA580C", title: L(lang, "QG de groupe", "Group HQ"),
      body: L(lang, "Passez à l'échelle sur plusieurs entités, marques, biens, portefeuilles et équipes quand votre univers se complexifie.",
                    "Scale across entities, brands, properties, portfolios, and teams when your world gets more complex.") },
  ];

  const features = [
    {
      icon: Bell, accent: "#F59E0B",
      eyebrow: L(lang, "Clarté", "Clarity"),
      title: L(lang, "Sachez ce qui mérite votre attention avant que ça devienne un problème", "Know what needs your attention before it becomes a problem"),
      body: L(lang, "Suivez factures, loyers, contrats, échéances et tâches : à partir de ce que vous saisissez, Cockpit OS fait remonter alertes, priorités et indicateurs de santé au bon moment.",
                    "Track invoices, rents, contracts, deadlines and tasks: from what you record, Cockpit OS surfaces alerts, priorities and health pulses at the right time."),
      points: [
        L(lang, "Priorités intelligentes : quoi faire ensuite", "Smart priorities: what to do next"),
        L(lang, "Alertes sur les factures, contrats & loyers que vous suivez", "Alerts on the invoices, contracts & rent you track"),
        L(lang, "Pouls de santé par entité", "Health pulse per entity"),
      ],
      visual: (
        <MiniVisual>
          <div className="space-y-2">
            {[
              { c: "#EF4444", t: L(lang, "Facture en retard : €3 200", "Invoice overdue: €3,200") },
              { c: "#F59E0B", t: L(lang, "Contrat expire dans 12 j", "Contract expires in 12d") },
              { c: "#2563EB", t: L(lang, "Objectif T2 à mi-parcours", "Q2 goal at halfway") },
              { c: "#10B981", t: L(lang, "5 tâches terminées aujourd'hui", "5 tasks done today") },
            ].map((a) => (
              <div key={a.t} className="flex items-center gap-2.5 rounded-lg border border-slate-200 bg-white px-3 py-2">
                <span className="w-2 h-2 rounded-full shrink-0" style={{ background: a.c }} />
                <span className="text-xs text-slate-700">{a.t}</span>
              </div>
            ))}
          </div>
        </MiniVisual>
      ),
    },
    {
      icon: KanbanSquare, accent: "#2563EB",
      eyebrow: L(lang, "Exécution", "Execution"),
      title: L(lang, "Tableaux, projets et objectifs qui font avancer le travail", "Boards, projects, and goals that keep work moving"),
      body: L(lang, "Planifiez vos projets, suivez vos tâches, gérez les jalons, créez des tâches récurrentes et reliez l'exécution à vos objectifs.",
                    "Plan projects, track tasks, manage milestones, set up recurring tasks, and connect execution back to goals."),
      points: [
        L(lang, "Vues Tableau, Kanban, Gantt & dashboard", "Table, Kanban, Gantt & dashboard views"),
        L(lang, "Tâches récurrentes & rappels", "Recurring tasks & reminders"),
        L(lang, "OKR reliés au travail réel", "OKRs linked to real work"),
      ],
      visual: (
        <MiniVisual>
          <div className="space-y-1.5">
            {[["#10B981", "Done"], ["#EA580C", "Stuck"], ["#2563EB", "Working"], ["#F59E0B", "To do"]].map(([c, s], i) => (
              <div key={i} className="flex items-center gap-2 rounded-md border border-slate-200 bg-white px-2.5 py-2">
                <span className="w-3.5 h-3.5 rounded-sm" style={{ background: c }} />
                <div className="h-1.5 flex-1 rounded bg-slate-100" />
                <span className="text-[10px] px-2 py-0.5 rounded text-white font-medium" style={{ background: c }}>{s}</span>
              </div>
            ))}
          </div>
        </MiniVisual>
      ),
    },
    {
      icon: Layers, accent: "#8B5CF6",
      eyebrow: L(lang, "Structure", "Structure"),
      title: L(lang, "Organisez le travail en Espaces", "Organize work into Spaces"),
      body: L(lang, "À l'intérieur de chaque entité, le travail vit dans des Espaces : des zones dédiées comme Finance, Ventes, Marketing, Delivery ou Juridique, chacune avec ses objectifs, tableaux, tâches et documents. Chaque modèle arrive avec ses Espaces prêts à l'emploi.",
                    "Inside each entity, work lives in Spaces: focused areas like Finance, Sales, Marketing, Delivery or Legal, each with its own goals, boards, tasks and documents. Every template comes with its Spaces ready to go."),
      points: [
        L(lang, "Espaces prêts à l'emploi : Finance, Ventes, Marketing, Delivery, Juridique…", "Ready-made Spaces: Finance, Sales, Marketing, Delivery, Legal…"),
        L(lang, "Chaque Espace a ses objectifs, tableaux & docs", "Each Space has its own goals, boards & docs"),
        L(lang, "Naviguez du groupe → entité → espace → projet", "Drill from group → entity → space → project"),
      ],
      visual: (
        <MiniVisual>
          <div className="space-y-2">
            <div className="flex items-center gap-2 text-xs font-semibold text-slate-700">
              <Briefcase className="w-3.5 h-3.5 text-emerald-500" /> {L(lang, "Conseil (entité)", "Consulting (entity)")}
            </div>
            {getTemplateSpaceKeys("consulting").map((key, i) => {
              const sp = SPACES_BY_KEY[key];
              const SpIcon = getSpaceIcon(sp.icon);
              return (
                <div key={key} className="ml-4 flex items-center gap-2 rounded-lg border border-slate-200 bg-white px-3 py-2">
                  <SpIcon className="w-3.5 h-3.5 shrink-0" style={{ color: ACCENTS[i % ACCENTS.length] }} />
                  <span className="text-xs text-slate-700">{sp.label[lang]}</span>
                  <span className="ml-auto text-[9px] text-slate-400">{L(lang, "objectifs · tableaux", "goals · boards")}</span>
                </div>
              );
            })}
          </div>
        </MiniVisual>
      ),
    },
    {
      icon: Wallet, accent: "#10B981",
      eyebrow: L(lang, "Finances", "Money"),
      title: L(lang, "Suivez trésorerie, actifs, dettes et valeur nette", "Track cash, assets, liabilities, and net worth"),
      body: L(lang, "Suivez votre cashflow, la valeur de votre portefeuille, vos actifs, vos dettes et vos avoirs multi-devises, pour votre monde personnel comme professionnel.",
                    "Follow cashflow, portfolio value, assets, liabilities, and multi-currency holdings across your personal or business world."),
      points: [
        L(lang, "Cashflow & mouvements financiers", "Cashflow & financial movement"),
        L(lang, "Portefeuille, actifs & dettes", "Portfolio, assets & liabilities"),
        L(lang, "Import CSV de l'historique courtier · multi-devises (EUR / USD)", "CSV import of broker history · multi-currency (EUR / USD)"),
      ],
      visual: (
        <MiniVisual>
          <div className="flex items-end gap-1.5 h-28 mb-3">
            {[30, 45, 40, 60, 55, 72, 68, 85, 80, 95].map((h, i) => (
              <div key={i} className="flex-1 rounded-sm bg-emerald-500/80" style={{ height: `${h}%` }} />
            ))}
          </div>
          <div className="flex items-center justify-between text-sm">
            <span className="text-slate-500">{L(lang, "Valeur nette", "Net worth")}</span>
            <span className="font-semibold text-emerald-600 tabular-nums">€2,438,920 <TrendingUp className="w-3.5 h-3.5 inline" /></span>
          </div>
        </MiniVisual>
      ),
    },
    {
      icon: Users2, accent: "#EA580C",
      eyebrow: L(lang, "Relations", "Relationships"),
      title: L(lang, "Vos relations et opportunités, en contexte", "Relationships and opportunities in context"),
      body: L(lang, "Suivez personnes, sociétés, deals, projets, factures et relances sans éparpiller vos données client entre plusieurs outils.",
                    "Track people, companies, deals, projects, invoices and follow-ups without scattering your customer data across tools."),
      points: [
        L(lang, "Pipeline de deals glisser-déposer", "Drag-and-drop deal pipeline"),
        L(lang, "Contacts & sociétés liés", "Linked contacts & companies"),
        L(lang, "Relances sur les deals dormants", "Follow-ups on aging deals"),
      ],
      visual: (
        <MiniVisual>
          <div className="grid grid-cols-3 gap-2">
            {[L(lang, "Lead", "Lead"), L(lang, "Offre", "Proposal"), L(lang, "Gagné", "Won")].map((col, i) => (
              <div key={col} className="rounded-lg border border-slate-200 bg-white p-2">
                <div className="text-[9px] text-slate-400 mb-2">{col}</div>
                {[1, 2].slice(0, 2 - (i === 2 ? 1 : 0)).map((n) => (
                  <div key={n} className="rounded-md bg-slate-50 border border-slate-200 p-1.5 mb-1.5">
                    <div className="h-1 rounded bg-slate-200 mb-1" />
                    <div className="text-[9px] font-mono" style={{ color: ACCENTS[i] }}>€{(i + 1) * 12}k</div>
                  </div>
                ))}
              </div>
            ))}
          </div>
        </MiniVisual>
      ),
    },
  ];

  const faqs = [
    { q: L(lang, "Dois-je avoir plusieurs entreprises pour l'utiliser ?", "Do I need multiple businesses to use it?"),
      a: L(lang, "Non. Commencez avec un seul cockpit pour vous, un projet ou une entreprise, puis ajoutez des entités quand votre univers grandit.",
                 "No. Start with a single cockpit for yourself, a project, or one business, then add entities as your world grows.") },
    { q: L(lang, "Puis-je commencer gratuitement ?", "Can I start for free?"),
      a: L(lang, "Oui. Démarrez immédiatement sur le plan Gratuit, ou profitez d'un essai gratuit de 7 jours du plan Business (toutes les fonctionnalités), sans carte bancaire. À la fin de l'essai, votre compte bascule sur le plan Gratuit ; toutes vos données restent accessibles et vous souscrivez quand vous le souhaitez.",
                 "Yes. Start right away on the Free plan, or take a 7-day free trial of the Business plan (all features), no credit card required. When the trial ends, your account moves to the Free plan; all your data stays accessible and you can subscribe whenever you want.") },
    { q: L(lang, "Est-ce que ça se synchronise avec ma banque ou mes outils ?", "Does it sync with my bank or tools?"),
      a: L(lang, "Non, et c'est volontaire. Vous saisissez ce qui compte, ce qui garde vos données justes, privées et sans intégrations qui cassent. Pour le portefeuille, vous pouvez importer l'historique de votre courtier en CSV.",
                 "No, by design. You record what matters, which keeps your data accurate, private and free of integrations that break. For your portfolio, you can import your broker's history via CSV.") },
    { q: L(lang, "Qu'est-ce qu'un Espace ?", "What is a Space?"),
      a: L(lang, "Un Espace est une sous-section d'une entité (un département, un client, un projet…) avec ses propres objectifs, tableaux, tâches et documents, pour garder un univers complexe bien rangé.",
                 "A Space is a sub-section of an entity (a department, a client, a project…) with its own goals, boards, tasks and documents, to keep a complex world organized.") },
    { q: L(lang, "Qu'est-ce que le Conseil IA ?", "What is AI Advisory?"),
      a: L(lang, "Un appui à la décision : revues de performance (hebdo/mensuel) et, sur les plans Business et plus, des board meetings où des conseillers IA challengent vos hypothèses et transforment les décisions en actions.",
                 "Decision support: performance reviews (weekly/monthly) and, on Business plans and above, board meetings where AI advisors challenge your assumptions and turn decisions into actions.") },
    { q: L(lang, "En quoi est-ce différent d'un Notion ou d'un CRM ?", "How is this different from Notion or a CRM?"),
      a: L(lang, "Plus structuré qu'un Notion, plus large qu'un CRM et plus orienté fondateur qu'un simple outil de gestion de projet. Votre travail, votre argent, vos relations et vos décisions au même endroit.",
                 "More structured than Notion, broader than a CRM, and more founder-focused than a normal project tool. Your work, money, relationships and decisions in one place.") },
  ];

  return (
    <div ref={rootRef} className="force-light bg-white text-slate-900 min-h-screen" data-testid="landing-page">
      <Seo path="/"
        title={L(lang, "Cockpit OS - Le systeme d'exploitation des entrepreneurs multi-entites", "Cockpit OS - The operating system for multi-entity entrepreneurs")}
        description={L(lang, "Pilotez toutes vos activites depuis un seul cockpit : taches, projets, CRM, finances, actifs, documents et conseil IA. Heberge en Europe.", "Run every business you own from one cockpit: tasks, projects, CRM, finances, assets, documents and AI advisory. Hosted in Europe.")} />
      <MarketingNav />

      {/* Trial announcement banner */}
      <div className="bg-slate-900 text-white" data-testid="trial-banner">
        <div className="max-w-6xl mx-auto px-5 sm:px-8 py-2.5 flex items-center justify-center gap-2 text-center text-sm flex-wrap">
          <Sparkles className="w-4 h-4 text-blue-400 shrink-0" />
          <span className="font-semibold">{L(lang, "Essai gratuit de 7 jours", "7-day free trial")}</span>
          <span className="text-slate-500">·</span>
          <span className="text-slate-300">{L(lang, "aucune carte bancaire requise pour commencer", "no credit card required to start")}</span>
        </div>
      </div>

      {/* Hero */}
      <section className="relative overflow-hidden">
        <div className="absolute inset-0 bg-[radial-gradient(circle_at_20%_-10%,rgba(37,99,235,0.10),transparent_55%)]" />
        <div className="relative max-w-6xl mx-auto px-5 sm:px-8 pt-20 pb-24 grid lg:grid-cols-2 gap-14 items-center">
          <div className="animate-fade-up">
            <div className="inline-flex items-center gap-2 rounded-full border border-slate-200 bg-slate-50 px-3 py-1 text-xs text-slate-600 mb-6">
              <Sparkles className="w-3.5 h-3.5 text-blue-600" /> {L(lang, "Avec un conseil IA intégré", "Now with a built-in AI advisory board")}
            </div>
            <h1 className="font-heading font-black text-4xl sm:text-5xl lg:text-6xl tracking-tighter leading-[1.05] text-slate-900 mb-6">
              {L(lang, "Votre centre de commande pour ", "Your command center for ")}
              <span className="text-blue-600">{L(lang, "tout ce que vous construisez", "everything you're building")}</span>.
            </h1>
            <p className="text-lg text-slate-600 leading-relaxed max-w-xl mb-8">
              {L(lang, "Cockpit OS réunit vos tâches, projets, CRM, factures, documents, objectifs, actifs, alertes et conseil IA au même endroit, que vous vous gériez vous-même, une entreprise ou un groupe en croissance.",
                       "Cockpit OS brings your tasks, projects, CRM, invoices, documents, goals, assets, alerts, and AI advisory into one place, whether you're managing yourself, one business, or a growing group.")}
            </p>
            <div className="flex flex-wrap items-center gap-3">
              <button onClick={go} data-testid="hero-start-free"
                className="inline-flex items-center gap-2 px-6 py-3 rounded-lg bg-blue-600 text-white font-semibold hover:bg-blue-700 transition-colors shadow-sm shadow-blue-600/30">
                {L(lang, "Construire mon cockpit", "Build your cockpit")} <ArrowRight className="w-4 h-4" />
              </button>
              <a href="#features" data-testid="hero-see-how"
                className="inline-flex items-center gap-2 px-6 py-3 rounded-lg border border-slate-300 text-slate-900 font-semibold hover:bg-slate-50 transition-colors">
                {L(lang, "Voir comment ça marche", "See how it works")}
              </a>
            </div>
            <div className="flex items-center gap-5 mt-7 text-xs text-slate-500">
              <span className="inline-flex items-center gap-1.5"><Sparkles className="w-3.5 h-3.5 text-blue-600" /> {L(lang, "Essai gratuit de 7 jours", "7-day free trial")}</span>
              <span className="inline-flex items-center gap-1.5"><Check className="w-3.5 h-3.5 text-emerald-500" /> {L(lang, "Sans carte bancaire", "No credit card")}</span>
              <span className="inline-flex items-center gap-1.5"><ShieldCheck className="w-3.5 h-3.5 text-emerald-500" /> {L(lang, "Privé par défaut", "Private by default")}</span>
            </div>
          </div>
          <div className="animate-fade-up" style={{ animationDelay: "120ms" }}><HeroMock lang={lang} /></div>
        </div>
      </section>

      {/* Stat strip */}
      <section className="border-y border-slate-200 bg-slate-50">
        <div className="max-w-6xl mx-auto px-5 sm:px-8 py-8 grid grid-cols-2 md:grid-cols-4 md:divide-x md:divide-slate-200">
          {[
            { v: L(lang, "Tout-en-un", "All-in-one"), l: L(lang, "Travail, argent, CRM, décisions", "Work, money, CRM, decisions") },
            { v: "14", l: L(lang, "Conseillers IA", "AI advisors") },
            { v: "1 → ∞", l: L(lang, "Du solo à un groupe entier", "From solo to a full group") },
            { v: "FR / EN", l: L(lang, "Bilingue, prêt pour l'UE", "Bilingual, EU-ready") },
          ].map((s) => (
            <div key={s.l} className="md:px-6 first:md:pl-0">
              <div className="font-heading font-black text-2xl sm:text-3xl tracking-tight text-slate-900">{s.v}</div>
              <div className="text-xs text-slate-500 mt-1">{s.l}</div>
            </div>
          ))}
        </div>
      </section>

      {/* Who it's for */}
      <section className="bg-white">
        <div className="max-w-6xl mx-auto px-5 sm:px-8 py-24">
          <div className="max-w-2xl mb-12">
            <div className="text-xs font-mono uppercase tracking-[0.2em] text-blue-600 font-semibold mb-3">{L(lang, "Pour qui", "Who it's for")}</div>
            <h2 className="font-heading font-bold text-3xl sm:text-4xl tracking-tight text-slate-900 mb-4">
              {L(lang, "Commencez simple. Évoluez quand il le faut.", "Start simple. Scale when needed.")}
            </h2>
            <p className="text-slate-600 text-lg">
              {L(lang, "Un seul cockpit qui s'adapte, de votre vie perso à un groupe complet d'entreprises.",
                       "One cockpit that adapts, from your personal life to a full group of companies.")}
            </p>
          </div>
          <div className="grid md:grid-cols-3 gap-5">
            {personas.map((p) => <PersonaCard key={p.title} {...p} />)}
          </div>
        </div>
      </section>

      {/* Entities — the building blocks */}
      <section className="bg-slate-50 border-y border-slate-200">
        <div className="max-w-6xl mx-auto px-5 sm:px-8 py-24">
          <div className="max-w-2xl mb-12">
            <div className="text-xs font-mono uppercase tracking-[0.2em] text-blue-600 font-semibold mb-3">{L(lang, "Les briques de base", "The building blocks")}</div>
            <h2 className="font-heading font-bold text-3xl sm:text-4xl tracking-tight text-slate-900 mb-4">
              {L(lang, "Commencez par une entité", "Start with an entity")}
            </h2>
            <p className="text-slate-600 text-lg">
              {L(lang, "Une entité, c'est un monde que vous pilotez : vous-même, une société, un portefeuille immobilier, une boutique. Choisissez un modèle et Cockpit OS installe les bons outils et Espaces. Ajoutez-en autant que nécessaire.",
                       "An entity is one world you run: yourself, a company, a property portfolio, a store. Pick a template and Cockpit OS sets up the right tools and Spaces. Add as many as you need.")}
            </p>
          </div>
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-4" data-testid="entity-templates">
            {TEMPLATES.map((tp) => {
              const Icon = tp.icon;
              return (
                <div key={tp.key} data-testid={`entity-template-${tp.key}`}
                  className="rounded-2xl border border-slate-200 bg-white p-5 hover:-translate-y-1 hover:shadow-lg transition-all duration-300">
                  <div className="w-11 h-11 rounded-xl flex items-center justify-center mb-4" style={{ background: `${tp.color}14`, color: tp.color }}>
                    <Icon className="w-5 h-5" />
                  </div>
                  <h3 className="font-heading font-bold text-base text-slate-900 mb-1.5">{tp.label[lang]}</h3>
                  <p className="text-[13px] text-slate-600 leading-relaxed">{tp.desc[lang]}</p>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* One cockpit, every lever */}
      <section id="features" className="bg-white">
        <div className="max-w-6xl mx-auto px-5 sm:px-8 py-24 space-y-24">
          <div className="max-w-2xl" data-parallax="up">
            <div className="text-xs font-mono uppercase tracking-[0.2em] text-blue-600 font-semibold mb-3">{L(lang, "La plateforme", "The platform")}</div>
            <h2 className="font-heading font-bold text-3xl sm:text-4xl tracking-tight text-slate-900 mb-4">
              {L(lang, "Un cockpit, tous les leviers", "One cockpit, every lever")}
            </h2>
            <p className="text-slate-600 text-lg">
              {L(lang, "Réunissez les pièces mobiles de votre travail et de votre business dans une vue claire.",
                       "Bring the moving parts of your work and business into one clear view.")}
            </p>
          </div>
          {features.map((f, i) => <FeatureRow key={f.title} {...f} reverse={i % 2 === 1} />)}
        </div>
      </section>

      {/* OKR philosophy: work on what matters */}
      <section className="bg-slate-50 border-y border-slate-200">
        <div className="max-w-6xl mx-auto px-5 sm:px-8 py-24 grid lg:grid-cols-2 gap-12 lg:gap-16 items-center">
          <div>
            <div className="inline-flex items-center gap-2 text-xs font-mono uppercase tracking-[0.2em] text-blue-600 font-semibold mb-3">
              <Target className="w-4 h-4" /> {L(lang, "Objectifs & résultats clés", "Objectives & key results")}
            </div>
            <h2 className="font-heading font-bold text-3xl sm:text-4xl tracking-tight text-slate-900 mb-4">
              {L(lang, "Travaillez sur ce qui compte vraiment", "Work on what actually matters")}
            </h2>
            <p className="text-slate-600 leading-relaxed mb-4">
              {L(lang, "Il est facile de rester occupé sans avancer. Les OKR vous gardent concentré sur les quelques résultats qui font vraiment bouger votre vie ou votre business : l'objectif dit ce que vous voulez, les résultats clés disent comment vous saurez que c'est atteint.",
                       "It's easy to stay busy without moving forward. OKRs keep you focused on the few outcomes that truly move your life or business: the objective says what you want, the key results say how you'll know you got there.")}
            </p>
            <p className="text-slate-600 leading-relaxed mb-7">
              {L(lang, "Dans Cockpit OS, les objectifs de vos Espaces se rattachent à ces résultats clés. Chaque tâche est donc reliée à un résultat mesurable : vous voyez immédiatement si votre travail quotidien fait progresser ce qui a le plus de valeur, et pas seulement votre liste de tâches.",
                       "In Cockpit OS, the goals inside your Spaces tie up to those key results. Every task ladders up to a measurable outcome, so you can instantly see whether your daily work is advancing what matters most, not just clearing a to-do list.")}
            </p>
            <div className="grid sm:grid-cols-3 gap-4">
              {[
                { icon: Target, t: L(lang, "Focus", "Focus"), d: L(lang, "Quelques priorités, pas mille.", "A few priorities, not a hundred.") },
                { icon: Layers, t: L(lang, "Alignement", "Alignment"), d: L(lang, "Le travail relié aux objectifs.", "Work linked to the goals.") },
                { icon: TrendingUp, t: L(lang, "Mesurable", "Measurable"), d: L(lang, "Des résultats, pas de l'activité.", "Outcomes, not activity.") },
              ].map((v) => (
                <div key={v.t} className="rounded-xl border border-slate-200 bg-white p-4">
                  <v.icon className="w-5 h-5 text-blue-600 mb-2" />
                  <div className="font-heading font-semibold text-slate-900 text-sm">{v.t}</div>
                  <p className="text-xs text-slate-600 mt-0.5 leading-relaxed">{v.d}</p>
                </div>
              ))}
            </div>
          </div>

          {/* OKR cascade visual */}
          <div className="rounded-2xl border border-slate-200 bg-white p-5 shadow-sm">
            <div className="rounded-xl bg-blue-600 text-white p-4 mb-3">
              <div className="text-[10px] uppercase tracking-wider text-blue-100 mb-1">{L(lang, "Objectif", "Objective")}</div>
              <div className="font-heading font-semibold text-sm">{L(lang, "Faire du conseil une activité rentable", "Make consulting a profitable arm")}</div>
              <div className="mt-3 h-1.5 rounded-full bg-white/25 overflow-hidden">
                <div className="h-full bg-white rounded-full" style={{ width: "62%" }} />
              </div>
              <div className="text-[10px] text-blue-100 mt-1 text-right">62%</div>
            </div>
            <div className="space-y-2.5 mb-3">
              {[
                { t: L(lang, "Atteindre 40k€ de MRR", "Reach €40k MRR"), v: "28 / 40", pct: 70 },
                { t: L(lang, "Signer 6 clients en régie", "Sign 6 retainer clients"), v: "4 / 6", pct: 66 },
              ].map((kr) => (
                <div key={kr.t} className="rounded-lg border border-slate-200 p-3">
                  <div className="flex items-center justify-between gap-2 mb-1.5">
                    <span className="text-xs font-medium text-slate-700">{kr.t}</span>
                    <span className="text-[11px] font-mono tabular-nums text-slate-500">{kr.v}</span>
                  </div>
                  <div className="h-1.5 rounded-full bg-slate-100 overflow-hidden">
                    <div className="h-full bg-emerald-500 rounded-full" style={{ width: `${kr.pct}%` }} />
                  </div>
                </div>
              ))}
            </div>
            <div className="text-[10px] uppercase tracking-wider text-slate-400 mb-1.5">{L(lang, "Objectifs liés dans les Espaces", "Linked goals in Spaces")}</div>
            <div className="flex flex-wrap gap-2">
              {[
                { g: L(lang, "Revoir le pricing", "Revamp pricing"), s: L(lang, "Ventes", "Sales") },
                { g: L(lang, "Programme de parrainage", "Referral program"), s: L(lang, "Marketing", "Marketing") },
                { g: L(lang, "Industrialiser le delivery", "Standardize delivery"), s: L(lang, "Produit", "Product") },
              ].map((x) => (
                <span key={x.g} className="inline-flex items-center gap-1.5 rounded-full border border-slate-200 bg-slate-50 px-2.5 py-1 text-[11px] text-slate-600">
                  <Check className="w-3 h-3 text-emerald-500" /> {x.g}
                  <span className="text-slate-400">· {x.s}</span>
                </span>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Philosophy: intentional manual input + CSV import */}
      <section className="bg-white">
        <div className="max-w-6xl mx-auto px-5 sm:px-8 py-24">
          <div className="grid lg:grid-cols-2 gap-12 lg:gap-16 items-start">
            <div>
              <div className="text-xs font-mono uppercase tracking-[0.2em] text-violet-600 font-semibold mb-3">{L(lang, "Notre philosophie", "Our philosophy")}</div>
              <h2 className="font-heading font-bold text-3xl sm:text-4xl tracking-tight text-slate-900 mb-4">
                {L(lang, "La saisie manuelle, par choix", "Manual input, on purpose")}
              </h2>
              <p className="text-slate-600 leading-relaxed mb-4">
                {L(lang, "Cockpit OS n'est pas branché en lecture automatique sur vos banques ou vos outils. Vous décidez de ce qui entre dans votre cockpit, et c'est justement ce qui le rend fiable.",
                         "Cockpit OS isn't wired to auto-pull from your banks or tools. You decide what enters your cockpit, and that's exactly what makes it trustworthy.")}
              </p>
              <p className="text-slate-600 leading-relaxed">
                {L(lang, "Saisir vos chiffres vous-même crée une vraie connaissance de votre activité : pas d'intégrations qui cassent, pas de données floues, juste une vision dont vous êtes maître. Et quand le volume grandit, l'import CSV prend le relais.",
                         "Recording your own numbers builds real understanding of your operation: no integrations that break, no fuzzy data, just a picture you own. And when volume grows, CSV import has your back.")}
              </p>
            </div>
            <div className="space-y-4">
              {[
                { icon: PenLine, accent: "#8B5CF6", t: L(lang, "Intentionnel par conception", "Intentional by design"),
                  d: L(lang, "Vous saisissez ce qui compte. Résultat : vous connaissez vraiment vos chiffres, pas seulement un tableau de bord importé.",
                              "You enter what matters. The result: you actually know your numbers, not just an imported dashboard.") },
                { icon: ShieldCheck, accent: "#10B981", t: L(lang, "Fiable & privé", "Reliable & private"),
                  d: L(lang, "Aucune connexion bancaire fragile, aucune fuite de données via des intégrations tierces. Vos données restent les vôtres.",
                              "No fragile bank connections, no data leaking through third-party integrations. Your data stays yours.") },
                { icon: Upload, accent: "#2563EB", t: L(lang, "Import CSV quand il le faut", "CSV import when you need it"),
                  d: L(lang, "Pour le portefeuille, importez l'historique d'opérations de votre courtier en CSV (colonnes détectées automatiquement) et exportez vos résultats.",
                              "For your portfolio, import your broker's transaction history via CSV (columns auto-detected) and export your results.") },
              ].map((c) => (
                <div key={c.t} className="flex gap-4 rounded-2xl border border-slate-200 bg-slate-50 p-5" data-testid={`philosophy-${c.t}`}>
                  <div className="w-10 h-10 rounded-xl flex items-center justify-center shrink-0" style={{ background: `${c.accent}14`, color: c.accent }}>
                    <c.icon className="w-5 h-5" />
                  </div>
                  <div>
                    <div className="font-heading font-semibold text-slate-900">{c.t}</div>
                    <p className="text-sm text-slate-600 mt-1 leading-relaxed">{c.d}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Privacy-first strip */}
      <section className="bg-white border-y border-slate-200" data-testid="landing-privacy-section">
        <div className="max-w-5xl mx-auto px-5 sm:px-8 py-16">
          <div className="flex flex-col items-start gap-5 sm:flex-row sm:items-center">
            <div className="shrink-0 w-12 h-12 rounded-2xl bg-emerald-500/10 flex items-center justify-center">
              <ShieldCheck className="w-6 h-6 text-emerald-600" />
            </div>
            <div>
              <h2 className="font-heading font-bold text-2xl tracking-tight text-slate-900 mb-2">
                {L(lang, "La confidentialité d'abord. Vos données vous appartiennent.", "Privacy first. Your data is yours.")}
              </h2>
              <p className="text-slate-600 leading-relaxed">
                {L(lang,
                  "Aucun cookie de suivi, aucun outil d'analyse publicitaire : uniquement un cookie de session essentiel. Vos données sont hébergées en Europe, exportables à tout moment, et supprimables définitivement en un clic.",
                  "No tracking cookies, no advertising analytics: just an essential session cookie. Your data is hosted in Europe, exportable anytime, and permanently deletable in one click.")}
              </p>
              <div className="flex flex-wrap gap-x-5 gap-y-2 mt-4 text-sm font-medium text-slate-700">
                <span className="inline-flex items-center gap-1.5"><Check className="w-4 h-4 text-emerald-500" />{L(lang, "Hébergé en Europe", "Hosted in Europe")}</span>
                <span className="inline-flex items-center gap-1.5"><Check className="w-4 h-4 text-emerald-500" />{L(lang, "Aucun pistage", "No tracking")}</span>
                <span className="inline-flex items-center gap-1.5"><Check className="w-4 h-4 text-emerald-500" />{L(lang, "Export & suppression à tout moment", "Export & delete anytime")}</span>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* AI Advisory band (high-contrast dark tension break) */}
      <section className="bg-slate-900 relative overflow-hidden">
        <div className="absolute inset-0 bg-[radial-gradient(circle_at_80%_50%,rgba(16,185,129,0.12),transparent_50%)]" />
        <div className="relative max-w-6xl mx-auto px-5 sm:px-8 py-24 grid lg:grid-cols-2 gap-14 items-center">
          <div>
            <div className="inline-flex items-center gap-2 text-xs font-mono uppercase tracking-[0.2em] text-emerald-400 mb-4">
              <Sparkles className="w-4 h-4" /> {L(lang, "Conseil IA", "AI Advisory")}
            </div>
            <h2 className="font-heading font-bold text-3xl sm:text-4xl tracking-tight text-white mb-4">
              {L(lang, "Un conseil pour de meilleures décisions", "An advisory board for better decisions")}
            </h2>
            <p className="text-slate-300 leading-relaxed mb-6">
              {L(lang, "Lancez des revues de performance, des revues hebdomadaires et mensuelles, ou des board meetings où des conseillers IA challengent vos hypothèses, révèlent les risques et transforment les décisions en actions.",
                       "Run performance reviews, weekly and monthly reviews, or board meetings where AI advisors challenge your assumptions, surface risks, and turn decisions into actions.")}
            </p>
            <div className="space-y-3 mb-7" data-testid="advisory-llm-notes">
              <div className="flex items-start gap-3 rounded-xl border border-emerald-400/20 bg-emerald-400/5 px-4 py-3">
                <Zap className="w-4 h-4 text-emerald-400 mt-0.5 shrink-0" />
                <p className="text-sm text-slate-200">
                  {L(lang, "Tarifs préférentiels sur les tokens Anthropic et les derniers modèles, inclus dans votre plan.",
                           "Preferential rates on Anthropic tokens and the latest models, included in your plan.")}
                </p>
              </div>
              <div className="flex items-start gap-3 rounded-xl border border-white/10 bg-white/[0.03] px-4 py-3">
                <KeyRound className="w-4 h-4 text-sky-300 mt-0.5 shrink-0" />
                <p className="text-sm text-slate-200">
                  {L(lang, "Ou utilisez vos propres clés OpenAI, Anthropic ou Gemini : usage illimité, facturé par votre fournisseur.",
                           "Or bring your own OpenAI, Anthropic or Gemini keys: unlimited usage, billed by your provider.")}
                </p>
              </div>
            </div>
            <button onClick={go} data-testid="advisory-cta"
              className="inline-flex items-center gap-2 px-5 py-2.5 rounded-lg bg-emerald-500 text-white font-semibold hover:bg-emerald-600 transition-colors">
              {L(lang, "Découvrir le Conseil", "Explore Advisory")} <ArrowRight className="w-4 h-4" />
            </button>
          </div>
          <div className="rounded-2xl border border-white/10 bg-slate-950/70 p-5 font-mono text-xs shadow-2xl">
            <div className="flex items-center gap-2 mb-3 text-slate-400">
              <Target className="w-3.5 h-3.5 text-emerald-400" /> {L(lang, "Revue en cours…", "Review in progress…")}
            </div>
            {[
              L(lang, "> Directeur financier : marge en baisse de 6% ce trimestre.", "> CFO: margin down 6% this quarter."),
              L(lang, "> Chief of Staff : relancer le pipeline cette semaine en priorité.", "> Chief of Staff: revive the pipeline this week as a priority."),
              L(lang, "> Conseiller fiscal : échéance TVA dans 9 jours.", "> Tax advisor: VAT deadline in 9 days."),
              L(lang, "> Verdict : avancer avec conditions (confiance 72%).", "> Verdict: proceed with conditions (72% confidence)."),
            ].map((line, i) => (
              <div key={i} className="py-1 border-b border-white/5 last:border-0 text-sky-300 animate-fade-in" style={{ animationDelay: `${i * 120}ms` }}>{line}</div>
            ))}
          </div>
        </div>
      </section>

      {/* FAQ */}
      <section id="faq" className="bg-white">
        <div className="max-w-3xl mx-auto px-5 sm:px-8 py-20">
          <h2 className="font-heading font-bold text-3xl tracking-tight text-slate-900 mb-8">{L(lang, "Questions fréquentes", "Frequently asked")}</h2>
          <div>
            {faqs.map((f, i) => <FaqItem key={i} q={f.q} a={f.a} testid={`landing-faq-${i}`} />)}
          </div>
        </div>
      </section>

      {/* Final CTA (solid blue band) */}
      <section className="bg-white pb-24">
        <div className="max-w-6xl mx-auto px-5 sm:px-8">
          <div className="rounded-3xl bg-blue-600 p-10 sm:p-16 text-center relative overflow-hidden">
            <div className="absolute inset-0 bg-[radial-gradient(circle_at_80%_20%,rgba(255,255,255,0.12),transparent_45%)]" />
            <div className="relative">
              <h2 className="font-heading font-black text-3xl sm:text-4xl tracking-tight text-white mb-4">
                {L(lang, "Prêt à construire votre cockpit ?", "Ready to build your cockpit?")}
              </h2>
              <p className="text-blue-100 text-lg max-w-xl mx-auto mb-8">
                {L(lang, "Mettez en place votre cockpit en quelques minutes. Gratuit pour démarrer.", "Set up your cockpit in minutes. Free to start.")}
              </p>
              <button onClick={go} data-testid="final-cta"
                className="inline-flex items-center gap-2 px-7 py-3.5 rounded-lg bg-white text-blue-600 font-semibold hover:bg-blue-50 transition-colors shadow-lg">
                {L(lang, "Construire mon cockpit", "Build your cockpit")} <ArrowRight className="w-4 h-4" />
              </button>
            </div>
          </div>
        </div>
      </section>

      <MarketingFooter />
    </div>
  );
}
